import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Stack,
  Divider,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { API_CONSTANTS } from "../../constants/apiConstants";
import { UserService } from "../../services/UserService";

// Per-graph metadata keyed by filename. Add an entry here when publishing
// a new graph to analysis_prod/ on the backend.
const GRAPH_META = {
  "physicsAwards-1_network.html": {
    id: 3,
    title: "Physics Awards Co-Recipient Network (Curated 184, ≥2 awards)",
    dateAdded: "2026-04-26",
    tags: ["physics", "curated", "co-recipient", "≥2 awards"],
    description:
      "<ul>"
      + "<li>Source: 184 curated physics-related awards (APS, AAAS, name/notes match \"physics\")</li>"
      + "<li>Filter: people with ≥2 awards in this set</li>"
      + "<li>Oversized awards (>200 recipients) attached as ★ badges, not pair edges</li>"
      + "<li><b>2,806 researchers · 87,063 edges · 163 awards</b></li>"
      + "</ul>",
  },
  "physicsAwards-1_min3_network.html": {
    id: 4,
    title: "Physics Awards Co-Recipient Network (Curated 184, ≥3 awards)",
    dateAdded: "2026-04-26",
    tags: ["physics", "curated", "co-recipient", "≥3 awards"],
    description:
      "<ul>"
      + "<li>Same source as #3, tightened to people with ≥3 awards</li>"
      + "<li>Surfaces the most heavily decorated subset</li>"
      + "<li><b>1,227 researchers · 29,763 edges · 129 awards</b></li>"
      + "</ul>",
  },
  "physics_awards_network.html": {
    id: 2,
    title: "Physics Awards Co-Recipient Network (Full)",
    dateAdded: "2026-04-22",
    tags: ["physics", "full"],
    description:
      "<ul>"
      + "<li>Source: <code>physicsawards.xlsx</code></li>"
      + "<li>Filter: ≥3 awards per person, ≤100 recipients per award</li>"
      + "</ul>",
  },
  "plasma_physics_network.html": {
    id: 1,
    title: "Plasma Physics Co-Recipient Network",
    dateAdded: "2026-04-15",
    tags: ["plasma physics", "specialty"],
    description:
      "<ul>"
      + "<li>Source: <code>plasma_physics_awardees.csv</code></li>"
      + "<li>No filtering - small specialty dataset</li>"
      + "<li>Useful for spotting tightly knit communities</li>"
      + "</ul>",
  },
  "geology_min2_network.html": {
    id: 5,
    title: "Geology Co-Recipient Network (Compound Query)",
    dateAdded: "2026-04-27",
    tags: ["geology", "earth sciences", "compound query", "≥2 awards", "new"],
    description:
      "<ul>"
      + "<li>Source: 130 curated geology / earth-science awards</li>"
      + "<li>Filter: people with ≥2 awards in this set</li>"
      + "<li><b>555 researchers · 6,432 edges · 94 awards</b></li>"
      + "<li><b>NEW</b>: compound query builder - pills combine with <b>AND</b> / <b>OR</b> (e.g. <i>Award A AND Researcher X</i>)</li>"
      + "</ul>",
  },
  "geology_filtered_network.html": {
    id: 6,
    title: "Geology Co-Recipient Network (Hand-Curated)",
    dateAdded: "2026-04-28",
    tags: ["geology", "earth sciences", "compound query", "curated", "new"],
    description:
      "<ul>"
      + "<li>Source: <code>geology-filtered-awardees_4-28.xlsx</code> - hand-curated awardee list</li>"
      + "<li>65 person-award records across 4 distinct awards (grouped by <code>fk_award</code>)</li>"
      + "<li>Names canonicalized by <code>RaiseProjectID</code> - 9 spelling variants merged</li>"
      + "<li><b>27 researchers · 328 edges · 4 awards</b></li>"
      + "<li>Compound query builder included</li>"
      + "</ul>",
  },
  "amchester_network.html": {
    id: 7,
    title: "AMChester Co-Recipient Network",
    dateAdded: "2026-04-28",
    tags: ["amchester", "compound query", "new"],
    description:
      "<ul>"
      + "<li>Source: <code>AMChester.xlsx</code></li>"
      + "<li>37 person-award records, grouped by <code>fk_award</code></li>"
      + "<li>Names canonicalized by <code>RaiseProjectID</code> - 2 spelling variants merged</li>"
      + "<li><b>32 researchers · 262 edges · 2 awards</b></li>"
      + "<li>Compound query builder included</li>"
      + "</ul>",
  },
};

const FALLBACK_META = {
  id: "-",
  title: null,
  dateAdded: null,
  tags: [],
  description: "No description provided.",
};

const TABLE_COLUMNS = [
  { id: "id", label: "#", width: "56px", align: "left", sortable: true },
  { id: "title", label: "Name", width: "260px", align: "left", sortable: true },
  { id: "description", label: "Description", width: "auto", align: "left", sortable: false },
  { id: "dateAdded", label: "Added", width: "130px", align: "left", sortable: true },
  { id: "modified", label: "Last Modified", width: "170px", align: "left", sortable: true },
];

const Analyses = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("desc");
  // Analysis HTML is fetched with an Authorization header (not a ?token= URL)
  // and rendered via srcDoc, so the JWT never lands in a URL / access log (#3).
  const [viewerHtml, setViewerHtml] = useState("");
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState("");

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await fetch(
          `${API_CONSTANTS.BASE_URL}${API_CONSTANTS.GET_ANALYSES}`,
          {
            headers: { Authorization: `Bearer ${UserService.accessToken}` },
          }
        );
        if (!res.ok) throw new Error("Failed to load");
        setFiles(await res.json());
      } catch {
        setError("Could not load analyses. Are you logged in?");
      }
    };
    fetchFiles();
  }, []);

  // Listen for "close-analysis" postMessage from the embedded graph iframe.
  // The srcDoc iframe is sandboxed without allow-same-origin, so its messages
  // arrive with origin "null"; we intentionally match on message shape (not
  // origin) so the in-graph close button keeps working.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data && e.data.type === "close-analysis") setViewing(null);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Fetch the selected analysis HTML with an Authorization header, then render
  // it via srcDoc. Replaces the old iframe src=...?token=<jwt> path (#3).
  useEffect(() => {
    if (!viewing) {
      setViewerHtml("");
      setViewerError("");
      return;
    }
    let cancelled = false;
    setViewerLoading(true);
    setViewerError("");
    setViewerHtml("");
    (async () => {
      try {
        const res = await fetch(
          `${API_CONSTANTS.BASE_URL}${API_CONSTANTS.GET_ANALYSIS}/${encodeURIComponent(viewing)}`,
          { headers: { Authorization: `Bearer ${UserService.accessToken}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!cancelled) setViewerHtml(text);
      } catch {
        if (!cancelled) setViewerError("Could not load this analysis. Are you logged in?");
      } finally {
        if (!cancelled) setViewerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewing]);

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatShortDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const displayName = (filename) => {
    return filename.replace(/\.html?$/i, "").replace(/[_-]/g, " ");
  };

  const metaFor = (file) => {
    const meta = GRAPH_META[file.filename] || {};
    return {
      id: meta.id ?? FALLBACK_META.id,
      title: meta.title || displayName(file.filename),
      description: meta.description || FALLBACK_META.description,
      tags: meta.tags || [],
      dateAdded: meta.dateAdded || null,
      modified: file.modified,
      filename: file.filename,
    };
  };

  const rows = useMemo(() => files.map(metaFor), [files]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      // Coerce undefineds to extreme so they sort last
      if (av === undefined || av === null || av === "-") av = order === "asc" ? Infinity : -Infinity;
      if (bv === undefined || bv === null || bv === "-") bv = order === "asc" ? Infinity : -Infinity;
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return order === "asc" ? -1 : 1;
      if (av > bv) return order === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  // Full-screen viewer
  if (viewing) {
    return (
      <Box sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999, bgcolor: "#000" }}>
        <IconButton
          onClick={() => setViewing(null)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10000,
            bgcolor: "rgba(0,0,0,0.7)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        {(viewerLoading || viewerError) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="body1">
              {viewerError || "Loading analysis…"}
            </Typography>
          </Box>
        )}
        {!viewerError && viewerHtml && (
          // sandbox WITHOUT allow-same-origin: the graph JS runs but cannot reach
          // the parent origin's localStorage where the JWT lives (#8/#10).
          <iframe
            title={viewing}
            srcDoc={viewerHtml}
            sandbox="allow-scripts allow-popups"
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          mb: 3,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
            theme.palette.primary.main,
            0.04
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "6px",
              bgcolor: alpha(theme.palette.primary.main, 0.18),
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <InsightsRoundedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
              Analyses
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Interactive co-recipient networks and graph analyses. Click a row to open the
              full-screen viewer.
            </Typography>
          </Box>
          <Chip
            label={`${files.length} ${files.length === 1 ? "graph" : "graphs"}`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<HistoryRoundedIcon />}
            onClick={() => navigate("/update-log")}
            sx={{ fontWeight: 600, textTransform: "none", whiteSpace: "nowrap" }}
          >
            Update Log
          </Button>
        </Stack>
      </Paper>

      {/* How to use */}
      <Accordion
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: "6px",
          border: `1px solid ${theme.palette.divider}`,
          "&:before": { display: "none" },
          "&.Mui-expanded": { my: 0, mb: 3 },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HelpOutlineRoundedIcon fontSize="small" color="primary" />
            <Typography fontWeight={600}>How to use</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" component="div" color="text.secondary">
            <ul style={{ marginTop: 0, paddingLeft: 20, lineHeight: 1.7 }}>
              <li>Click any row in the table below to open the interactive graph full-screen.</li>
              <li>
                <b>Search</b> (top-left panel) - type a researcher's name to focus and highlight that
                node. The <b>Selected Researcher</b> section shows their awards and co-recipients,
                grouped by award. Click any co-recipient name to jump to that node.
              </li>
              <li>
                <b>Awards</b> (top-right panel) - toggle individual awards on/off to filter edges.
                Use <b>Min shared awards</b> buttons to hide pairs sharing fewer than N awards.
              </li>
              <li>
                <b>Graph</b> - drag nodes to reposition; scroll to zoom; hover over edges to see
                which awards are shared.
              </li>
              <li>Click the red ✕ beside the graph title (or the ✕ in the top-right) to close the viewer.</li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: "6px",
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: theme.palette.error.main,
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {files.length === 0 && !error ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ p: 4, textAlign: "center", borderRadius: "6px" }}
        >
          <Typography color="text.secondary">No analyses uploaded yet.</Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          variant="outlined"
          sx={{ borderRadius: "6px", overflow: "hidden" }}
        >
          <Table sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: "text.secondary",
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  },
                }}
              >
                {TABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{ width: col.width, verticalAlign: "middle", py: 1.5 }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow
                  key={row.filename}
                  hover
                  onClick={() => setViewing(row.filename)}
                  sx={{
                    cursor: "pointer",
                    transition: "background 0.15s",
                    "& td": { verticalAlign: "top", py: 2 },
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  <TableCell
                    sx={{
                      color: "text.secondary",
                      fontFamily: "monospace",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, lineHeight: 1.35 }}>
                      {row.title}
                    </Typography>
                    {row.tags.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {row.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: theme.palette.primary.main,
                              border: "none",
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "text.secondary",
                      fontSize: 13,
                      lineHeight: 1.6,
                      whiteSpace: "normal",
                    }}
                  >
                    <Box
                      component="div"
                      sx={{
                        "& ul": { margin: 0, paddingLeft: "18px" },
                        "& li": { marginBottom: "2px" },
                        "& code": {
                          fontSize: "0.85em",
                          backgroundColor: "action.hover",
                          padding: "1px 4px",
                          borderRadius: "3px",
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: row.description }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: 13 }}>
                    {formatShortDate(row.dateAdded)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: 13, color: "text.secondary" }}>
                    {formatDate(row.modified)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Analyses;
